import React, { useEffect } from "react";
import "./App.css";

import logo from "./assets/logo.svg";              // 200×80
import phoneMock from "./assets/phone-mock.png";   // PNG phone mockup
import googlePlay from "./assets/google-play.svg"; // SVG badge
import appStore from "./assets/app-store.svg";     // SVG badge

/* Store links */
const GOOGLE_PLAY =
  "https://play.google.com/store/apps/details?id=com.osl.ejotno";
const APP_STORE =
  "https://apps.apple.com/us/app/ejotno/id6741751239";

/* OPTIONAL: Deep link schemes (configure in your Android/iOS apps) */
const ANDROID_DEEP_LINK = "ejotno://home";
const IOS_DEEP_LINK = "ejotno://home";

/* ------------------ Helper: detect platform ------------------ */
function detectPlatform() {
  if (typeof navigator === "undefined") {
    return { isAndroid: false, isIOS: false, isMobile: false };
  }

  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  return {
    isAndroid,
    isIOS,
    isMobile: isAndroid || isIOS,
  };
}

/* ------------------ OS Detection Page (/os_detection) ------------------ */
function OsDetectionPage() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const { isAndroid, isIOS, isMobile } = detectPlatform();

    const params = new URLSearchParams(window.location.search);
    // e.g. https://yourdomain.com/os_detection?no_redirect=1 (for debugging)
    if (params.get("no_redirect") === "1") return;

    // If not mobile, just send to homepage
    if (!isMobile) {
      window.location.replace("/");
      return;
    }

    let deepLink = null;
    let storeLink = null;

    if (isAndroid) {
      deepLink = ANDROID_DEEP_LINK;
      storeLink = GOOGLE_PLAY;
    } else if (isIOS) {
      deepLink = IOS_DEEP_LINK;
      storeLink = APP_STORE;
    } else {
      // Unknown mobile – fallback to homepage
      window.location.replace("/");
      return;
    }

    const start = Date.now();

    // 1️⃣ Try to open the app first (deep link)
    window.location.href = deepLink;

    // 2️⃣ After a short delay, if we are still here, go to the store
    const timeout = setTimeout(() => {
      const elapsed = Date.now() - start;
      if (elapsed < 1600) {
        window.location.href = storeLink;
      }
    }, 1400);

    return () => clearTimeout(timeout);
  }, []);

  // Minimal UI while redirecting
  return (
    <main className="os-detect">
      <div className="os-detect__inner">
        <p>Detecting your device and opening eJOTNO…</p>
      </div>
    </main>
  );
}

/* ------------------ Home Page (/) — your existing hero ------------------ */
function HomePage() {
  return (
    <main className="hero">
      <div className="hero__inner">
        {/* LEFT: logo + copy + badges */}
        <section aria-label="eJOTNO intro">
          {/* Mobile: center the logo at the very top */}
          <div className="brandbar">
            <img
              className="logo"
              src={logo}
              alt="eJOTNO"
              width={200}
              height={80}
            />
          </div>

          {/* Title & subtitle rewritten per description */}
          <h1 className="title">
            A technology platform connecting you to trusted, independent
            healthcare providers.
          </h1>
          <p className="subtitle">
            eJOTNO helps you find and communicate with verified third-party
            providers—doctors, caregivers, and therapists—based on your needs.
            We don&apos;t provide medical services or store health data; we
            simply bridge you to care.
          </p>

          {/* Store badges (SVG only) */}
          <div className="actions" aria-label="Download eJOTNO">
            <a
              className="store-btn"
              href={GOOGLE_PLAY}
              target="_blank"
              rel="noreferrer"
              aria-label="Get it on Google Play"
            >
              <img
                className="store-img"
                src={googlePlay}
                alt="Get it on Google Play"
              />
            </a>
            <a
              className="store-btn"
              href={APP_STORE}
              target="_blank"
              rel="noreferrer"
              aria-label="Download on the App Store"
            >
              <img
                className="store-img"
                src={appStore}
                alt="Download on the App Store"
              />
            </a>
          </div>

          <div className="footer">
            © {new Date().getFullYear()} eJOTNO
          </div>
        </section>

        {/* RIGHT: phone mockup */}
        <section className="visual" aria-label="App preview">
          <img
            className="mock"
            src={phoneMock}
            alt="eJOTNO app preview on phone"
          />
        </section>
      </div>
    </main>
  );
}

/* ------------------ Root App: choose view by pathname ------------------ */
export default function App() {
  const path =
    typeof window !== "undefined" ? window.location.pathname : "/";

  if (path === "/os_detection" || path === "/os_detection/") {
    return <OsDetectionPage />;
  }

  return <HomePage />;
}
